'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

export const dynamic = 'force-dynamic';

export default function HomePage() {
  const [hovered, setHovered] = useState<'barber' | 'rep' | null>(null);
  const [mounted, setMounted] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const handleMouse = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouse, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  const barberActive = hovered === 'barber';
  const repActive = hovered === 'rep';

  return (
    <div
      ref={containerRef}
      className="min-h-screen flex flex-col md:flex-row overflow-hidden relative"
      style={{ fontFamily: '"DM Sans", system-ui, sans-serif' }}
    >
      {/* ── LEFT: BARBER SIDE ── */}
      <Link
        href="/barber-landing"
        className="relative flex-1 flex flex-col items-center justify-center min-h-[50vh] md:min-h-screen cursor-pointer overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, hsl(220 22% 7%) 0%, hsl(225 18% 5%) 50%, hsl(240 22% 6%) 100%)',
          flex: mounted ? (barberActive ? '1.45' : repActive ? '0.55' : '1') : '1',
          transition: 'flex 0.65s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onMouseEnter={() => setHovered('barber')}
        onMouseLeave={() => setHovered(null)}
      >
        {/* Animated gold orb — follows mouse on hover */}
        <div
          className="absolute pointer-events-none transition-opacity duration-500"
          style={{
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, hsl(43 72% 55% / 0.18) 0%, hsl(43 72% 55% / 0.06) 40%, transparent 70%)',
            left: barberActive ? `${mousePos.x * 0.4}px` : '50%',
            top: barberActive ? `${mousePos.y * 0.4}px` : '50%',
            transform: 'translate(-50%, -50%)',
            opacity: barberActive ? 1 : 0.4,
            transition: barberActive ? 'opacity 0.4s ease, left 0.1s linear, top 0.1s linear' : 'opacity 0.6s ease, left 0.8s ease, top 0.8s ease',
          }}
        />

        {/* Grain texture */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 200px',
          }}
        />

        {/* Ghost watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span
            className="font-black select-none whitespace-nowrap"
            style={{
              fontSize: 'clamp(80px, 18vw, 260px)',
              color: 'transparent',
              WebkitTextStroke: '1px hsl(43 72% 55% / 0.055)',
              letterSpacing: '-0.04em',
              transform: barberActive ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            SPHERE
          </span>
        </div>

        {/* Subtle grid lines */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(hsl(43 72% 55% / 0.03) 1px, transparent 1px), linear-gradient(90deg, hsl(43 72% 55% / 0.03) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            opacity: barberActive ? 1 : 0,
            transition: 'opacity 0.6s ease',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center px-8 md:px-14 py-16 w-full max-w-lg mx-auto">

          {/* Live badge */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full mb-8"
            style={{
              background: 'hsl(43 72% 55% / 0.12)',
              border: '1px solid hsl(43 72% 55% / 0.25)',
              transform: barberActive ? 'translateY(-2px)' : 'translateY(0)',
              transition: 'transform 0.4s ease',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: 'hsl(43 72% 68%)' }}>
              Accepting Barbers — Seattle
            </span>
          </div>

          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8" style={{ background: 'linear-gradient(to right, transparent, hsl(43 72% 55%))' }} />
            <span className="text-[10px] font-bold tracking-[0.28em] uppercase" style={{ color: 'hsl(43 72% 62%)' }}>
              The Barber Sphere
            </span>
            <div className="h-px w-8" style={{ background: 'linear-gradient(to left, transparent, hsl(43 72% 55%))' }} />
          </div>

          {/* Headline */}
          <h2
            className="font-black leading-[0.95] mb-6 text-white"
            style={{ fontSize: 'clamp(38px, 5.5vw, 72px)', letterSpacing: '-0.04em' }}
          >
            Fill Your Chair.{' '}
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, hsl(43 72% 68%) 0%, hsl(35 90% 58%) 50%, hsl(43 72% 55%) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Every Week.
            </span>
          </h2>

          <p
            className="text-base leading-relaxed mb-4"
            style={{ color: 'hsl(220 5% 58%)', maxWidth: '300px', fontSize: '15px' }}
          >
            Pre-booked clients delivered to your chair. No marketing spend. No cold outreach. You just cut.
          </p>

          {/* Value props */}
          <div className="flex flex-col gap-2 mb-10 w-full max-w-[280px]">
            {[
              'Clients booked before they walk in',
              'Pay only for confirmed appointments',
              'Works with Booksy, Squire & more',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2.5 text-left">
                <span style={{ color: 'hsl(43 72% 62%)', fontSize: '12px' }}>✦</span>
                <span className="text-sm" style={{ color: 'hsl(220 5% 55%)' }}>{item}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div
            className="inline-flex items-center gap-3 px-9 py-4 rounded-xl font-bold text-sm w-full justify-center"
            style={{
              background: barberActive
                ? 'linear-gradient(135deg, hsl(43 72% 62%), hsl(35 90% 52%))'
                : 'linear-gradient(135deg, hsl(43 72% 58%), hsl(35 85% 50%))',
              color: 'hsl(220 15% 5%)',
              boxShadow: barberActive
                ? '0 12px 50px hsl(43 72% 55% / 0.5), 0 4px 16px hsl(43 72% 55% / 0.3)'
                : '0 4px 24px hsl(43 72% 55% / 0.25)',
              transform: barberActive ? 'scale(1.04) translateY(-1px)' : 'scale(1)',
              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              letterSpacing: '-0.01em',
            }}
          >
            <span>See How It Works</span>
            <span
              style={{
                transform: barberActive ? 'translateX(4px)' : 'translateX(0)',
                transition: 'transform 0.3s ease',
                fontSize: '16px',
              }}
            >→</span>
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-5 mt-8">
            <div className="flex -space-x-2">
              {['MJ', 'DW', 'JT'].map((initials, i) => (
                <div
                  key={initials}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-black border-2"
                  style={{
                    background: `hsl(${43 + i * 20} 72% ${45 + i * 5}%)`,
                    borderColor: 'hsl(220 22% 7%)',
                    color: 'hsl(220 15% 5%)',
                    zIndex: 3 - i,
                  }}
                >
                  {initials}
                </div>
              ))}
            </div>
            <div className="text-left">
              <div className="text-[11px] font-bold" style={{ color: 'hsl(220 5% 65%)' }}>
                <span style={{ color: 'hsl(43 72% 62%)' }}>20+ barbers</span> already booked
              </div>
              <div className="text-[10px]" style={{ color: 'hsl(220 5% 40%)' }}>Avg. 20 appointments/mo</div>
            </div>
          </div>
        </div>

        {/* Right edge glow */}
        <div
          className="hidden md:block absolute right-0 top-0 bottom-0 w-px pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, transparent 10%, hsl(43 72% 55% / 0.35) 50%, transparent 90%)',
            opacity: barberActive ? 1 : 0.5,
            transition: 'opacity 0.5s ease',
          }}
        />
      </Link>

      {/* ── CENTER DIVIDER (mobile) ── */}
      <div
        className="md:hidden h-px w-full"
        style={{ background: 'linear-gradient(to right, hsl(43 72% 55% / 0.5), hsl(340 40% 70% / 0.5))' }}
      />

      {/* ── CENTER BADGE (desktop) ── */}
      <div
        className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 items-center justify-center pointer-events-none"
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, hsl(220 22% 8%), hsl(220 18% 6%))',
          border: '1px solid hsl(43 72% 55% / 0.2)',
          boxShadow: '0 0 0 8px hsl(220 22% 7% / 0.8), 0 0 40px hsl(43 72% 55% / 0.15)',
          opacity: hovered ? 0 : 1,
          transition: 'opacity 0.4s ease',
        }}
      >
        <span style={{ color: 'hsl(43 72% 62%)', fontSize: '18px' }}>✦</span>
      </div>

      {/* ── RIGHT: BRAND AMBASSADOR SIDE ── */}
      <Link
        href="/rep-landing"
        className="relative flex-1 flex flex-col items-center justify-center min-h-[50vh] md:min-h-screen cursor-pointer overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #F2EAF0 0%, #F9F3F7 50%, #FBF6FA 100%)',
          flex: mounted ? (repActive ? '1.45' : barberActive ? '0.55' : '1') : '1',
          transition: 'flex 0.65s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onMouseEnter={() => setHovered('rep')}
        onMouseLeave={() => setHovered(null)}
      >
        {/* Animated rose orb */}
        <div
          className="absolute pointer-events-none"
          style={{
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(196,104,138,0.2) 0%, rgba(196,104,138,0.07) 40%, transparent 70%)',
            left: repActive ? `${(mousePos.x - (containerRef.current?.getBoundingClientRect().width ?? 0) / 2) * 0.3 + 50}%` : '50%',
            top: repActive ? `${mousePos.y * 0.3}px` : '50%',
            transform: 'translate(-50%, -50%)',
            opacity: repActive ? 1 : 0.5,
            transition: repActive ? 'opacity 0.4s ease, left 0.15s linear, top 0.15s linear' : 'opacity 0.6s ease, left 0.8s ease, top 0.8s ease',
          }}
        />

        {/* Ghost watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span
            className="font-black select-none whitespace-nowrap"
            style={{
              fontSize: 'clamp(80px, 18vw, 260px)',
              color: 'transparent',
              WebkitTextStroke: '1px rgba(196,104,138,0.07)',
              letterSpacing: '-0.04em',
              transform: repActive ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            PROMO
          </span>
        </div>

        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(196,104,138,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            opacity: repActive ? 0.8 : 0,
            transition: 'opacity 0.6s ease',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center px-8 md:px-14 py-16 w-full max-w-lg mx-auto">

          {/* Live badge */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full mb-8"
            style={{
              background: 'rgba(196,104,138,0.1)',
              border: '1px solid rgba(196,104,138,0.25)',
              transform: repActive ? 'translateY(-2px)' : 'translateY(0)',
              transition: 'transform 0.4s ease',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#C4688A' }} />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#C4688A' }}>
              Now Recruiting — Seattle
            </span>
          </div>

          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8" style={{ background: 'linear-gradient(to right, transparent, #C4688A)' }} />
            <span className="text-[10px] font-bold tracking-[0.28em] uppercase" style={{ color: '#C4688A' }}>
              Promodelle Agency
            </span>
            <div className="h-px w-8" style={{ background: 'linear-gradient(to left, transparent, #C4688A)' }} />
          </div>

          {/* Headline */}
          <h2
            className="font-black leading-[0.95] mb-6"
            style={{ fontSize: 'clamp(38px, 5.5vw, 72px)', letterSpacing: '-0.04em', color: '#1a1a1a' }}
          >
            Turn Energy{' '}
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, #C4688A 0%, #A0507A 40%, #C9A96E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Into Income.
            </span>
          </h2>

          <p
            className="text-base leading-relaxed mb-4"
            style={{ color: '#777', maxWidth: '300px', fontSize: '15px' }}
          >
            Work Seahawks, Mariners, Kraken events. Get paid per confirmed booking. No cap, no ceiling, no boss.
          </p>

          {/* Value props */}
          <div className="flex flex-col gap-2 mb-10 w-full max-w-[280px]">
            {[
              'Paid weekly — results, not hours',
              'Live sporting events across Seattle',
              'Set your own schedule, stack your own money',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2.5 text-left">
                <span style={{ color: '#C4688A', fontSize: '12px' }}>✦</span>
                <span className="text-sm" style={{ color: '#888' }}>{item}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div
            className="inline-flex items-center gap-3 px-9 py-4 rounded-full font-bold text-sm w-full justify-center"
            style={{
              background: repActive ? '#111' : '#1a1a1a',
              color: '#fff',
              boxShadow: repActive
                ? '0 12px 50px rgba(26,26,26,0.35), 0 4px 16px rgba(196,104,138,0.2)'
                : '0 4px 24px rgba(26,26,26,0.15)',
              transform: repActive ? 'scale(1.04) translateY(-1px)' : 'scale(1)',
              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              letterSpacing: '-0.01em',
            }}
          >
            <span>See If You Qualify</span>
            <span
              style={{
                transform: repActive ? 'translateX(4px)' : 'translateX(0)',
                transition: 'transform 0.3s ease',
                fontSize: '16px',
              }}
            >→</span>
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-5 mt-8">
            <div className="flex -space-x-2">
              {['AK', 'JL', 'MR'].map((initials, i) => (
                <div
                  key={initials}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-black border-2"
                  style={{
                    background: `hsl(${340 + i * 15} ${50 + i * 8}% ${70 - i * 5}%)`,
                    borderColor: '#F9F3F7',
                    color: '#fff',
                    zIndex: 3 - i,
                  }}
                >
                  {initials}
                </div>
              ))}
            </div>
            <div className="text-left">
              <div className="text-[11px] font-bold" style={{ color: '#888' }}>
                <span style={{ color: '#C4688A' }}>Female-forward</span> promo team
              </div>
              <div className="text-[10px]" style={{ color: '#bbb' }}>Paid per confirmed booking</div>
            </div>
          </div>
        </div>

        {/* Left edge glow */}
        <div
          className="hidden md:block absolute left-0 top-0 bottom-0 w-px pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, transparent 10%, rgba(196,104,138,0.3) 50%, transparent 90%)',
            opacity: repActive ? 1 : 0.5,
            transition: 'opacity 0.5s ease',
          }}
        />
      </Link>

      {/* ── BOTTOM WORDMARK ── */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4">
        <span
          className="text-[10px] font-bold tracking-[0.22em] uppercase pointer-events-none"
          style={{ color: 'rgba(150,130,140,0.45)' }}
        >
          FindACut.com
        </span>
        <span style={{ color: 'rgba(150,130,140,0.3)', fontSize: '10px' }}>·</span>
        <Link
          href="/rep-login"
          className="text-[10px] font-bold tracking-[0.18em] uppercase transition-colors"
          style={{ color: 'rgba(196,104,138,0.55)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#C4688A')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(196,104,138,0.55)')}
        >
          Rep Login
        </Link>
      </div>
    </div>
  );
}
